import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { animated, easings, useSpring } from 'react-spring';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { range } from '@sorare/core/src/lib/arrays';

import type { StreakProgressAnimated_thresholdsStreakTask } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  width: 100%;
  gap: 1px;
`;
const Cell = styled.div`
  flex: 1;
  height: var(--unit);
  background: var(--c-nd-200);
  border-radius: 2px;
`;
const AnimatedCellFill = styled(animated.div)`
  position: relative;
  background-color: var(--c-rivals-loss);
  border-radius: 2px;
  height: 100%;

  &.animatedCell {
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      background-color: var(--c-rivals-loss);
      border-radius: 2px;
      filter: blur(2px);
    }
  }
`;

type Props = {
  completedStreakTask: StreakProgressAnimated_thresholdsStreakTask;
  animate?: boolean;
  delay?: number;
};
export const StreakProgressAnimated = ({
  completedStreakTask,
  animate,
  delay = 0,
}: Props) => {
  const { progress, target } = completedStreakTask;

  const [{ width }] = useSpring(
    () => ({
      from: {
        width: 0,
      },
      ...(animate && { to: { width: 100 } }),
      config: {
        easing: easings.easeOutCubic,
        duration: 1500,
      },
      delay,
    }),
    [animate]
  );

  return (
    <Root>
      {range(target).map(i => {
        const cellWidth = i < progress - 1 ? '100%' : '0%';
        const animatedCell = i === progress - 1;

        return (
          <Cell key={i}>
            <AnimatedCellFill
              className={classNames({ animatedCell })}
              style={{
                width: animatedCell ? width.to(w => `${w}%`) : cellWidth,
              }}
            />
          </Cell>
        );
      })}
    </Root>
  );
};

StreakProgressAnimated.fragments = {
  thresholdsStreakTask: gql`
    fragment StreakProgressAnimated_thresholdsStreakTask on ThresholdsStreakTask {
      id
      progress
      target
    }
  ` as TypedDocumentNode<StreakProgressAnimated_thresholdsStreakTask>,
};
